import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  getCurrentUser,
  trackNodeVisit as apiTrackNodeVisit,
  toggleBookmark as apiToggleBookmark,
  getUserBookmarks as apiGetUserBookmarks,
  rateNode as apiRateNode,
  getUserRating as apiGetUserRating,
  getNodeRatingStats as apiGetNodeRatingStats,
  getUserReveals as apiGetUserReveals,
  revealNode as apiRevealNode,
  getUserSolutions as apiGetUserSolutions,
  setUserSolutions as apiSetUserSolutions
} from '../services/authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? '/api'  // Cloudflare Pages will serve from same domain
    : 'http://localhost:8787/api'  // Wrangler dev server
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Commencer en loading
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [revealedNodes, setRevealedNodes] = useState(new Set());
  // solutions: array of {node_id, position, title_en, title_fr, short_description_en, short_description_fr, color_code}
  const [solutions, setSolutions] = useState([]);

  // Fonction pour récupérer les informations utilisateur réelles
  const fetchUserInfo = useCallback(async () => {
    console.log('🔍 Fetching user info from API');
    try {
      const response = await getCurrentUser();
      
      console.log('📡 API Response:', response);
      
      if (response.authenticated) {
        console.log('✅ User authenticated:', response.name);
        setUser({
          id: response.id,
          googleId: response.googleId,
          userUuid: response.userUuid,
          name: response.name,
          email: response.email,
          pictureUrl: response.pictureUrl,
          isAdmin: response.isAdmin,
          isPremium: response.isPremium,
          joinedAt: response.joinedAt,
          lastLogin: response.lastLogin,
          welcome: response.welcome === true
        });
        setIsAuthenticated(true);
      } else {
        console.log('❌ User not authenticated');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.log('🚨 Error fetching user info:', error.message);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // NOTE: port 3000 used to short-circuit here with a hard-coded test user. It made
    // the UI look logged in while no session cookie existed, so every authenticated
    // endpoint answered 401 — and it re-ran after logout, instantly "logging back in".
    // The CRA dev server now goes through the real session like every other origin.

    // Clean up any legacy localStorage auth data
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');

    // Always fetch auth state from the backend via session cookie
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('login') === 'success') {
      // Clean the URL param after OAuth redirect
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    fetchUserInfo();
  }, [fetchUserInfo]);

  const login = async () => {
    // Google sign-in through Better Auth (same flow as the login modal)
    const { authClient } = await import('../services/authClient');
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: `${window.location.origin}/?login=success`,
    });
  };

  const logout = async () => {
    console.log('🚪 Starting logout process...');
    try {
      // Appeler l'endpoint de logout du backend
      await axios.get(`${API_BASE_URL}/auth/logout`, {
        withCredentials: true
      });
      console.log('✅ Backend logout successful');
    } catch (error) {
      console.log('⚠️ Logout error:', error);
    } finally {
      // Nettoyer l'état local
      setUser(null);
      setIsAuthenticated(false);
      console.log('🔄 Redirecting to home...');
      // Rediriger vers la page d'accueil
      window.location.href = '/';
    }
  };

  // Fonction pour mettre à jour les préférences utilisateur
  const updateUserPreference = async (key, value) => {
    if (!isAuthenticated) {
      throw new Error('User must be authenticated to update preferences');
    }

    try {
      // Pour l'instant, on simule la sauvegarde côté serveur
      // TODO: Implémenter l'API côté serveur pour sauvegarder les préférences
      console.log(`Updating user preference: ${key} = ${value}`);
      
      // Mettre à jour l'état local
      setUser(prevUser => ({
        ...prevUser,
        [`preferred${key.charAt(0).toUpperCase() + key.slice(1)}`]: value
      }));

      return true;
    } catch (error) {
      console.error('Failed to update user preference:', error);
      throw error;
    }
  };

  // Fonction pour rafraîchir les données utilisateur
  const refreshUser = useCallback(async () => {
    setLoading(true);
    await fetchUserInfo();
  }, [fetchUserInfo]);

  // Load revealed nodes when authenticated
  const loadRevealedNodes = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await apiGetUserReveals();
      if (data.revealed && data.revealed.length > 0) {
        setRevealedNodes(new Set(data.revealed));
      }
    } catch (error) {
      console.error('Failed to load revealed nodes:', error);
    }
  }, [isAuthenticated]);

  // Load reveals when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      loadRevealedNodes();
    } else {
      setRevealedNodes(new Set());
    }
  }, [isAuthenticated, loadRevealedNodes]);

  // Load solutions when authenticated
  const loadSolutions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await apiGetUserSolutions();
      setSolutions(data.solutions || []);
    } catch (error) {
      console.error('Failed to load solutions:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSolutions();
    } else {
      setSolutions([]);
    }
  }, [isAuthenticated, loadSolutions]);

  const toggleSolution = useCallback(async (nodeId, nodeData = {}) => {
    if (!isAuthenticated) return null;

    const existing = solutions.find(s => s.node_id === nodeId);
    let newSolutions;

    if (existing) {
      // Remove this solution and compact positions
      newSolutions = solutions
        .filter(s => s.node_id !== nodeId)
        .map((s, idx) => ({ ...s, position: idx + 1 }));
    } else {
      if (solutions.length >= 5) return null;
      const nextPos = solutions.length + 1;
      newSolutions = [
        ...solutions,
        { node_id: nodeId, position: nextPos, title_en: nodeData.title, title_fr: nodeData.title, short_description_en: null, short_description_fr: null, color_code: nodeData.color_code }
      ];
    }

    // Optimistic update
    setSolutions(newSolutions);

    try {
      const payload = newSolutions.map(s => ({ nodeId: s.node_id, position: s.position }));
      const data = await apiSetUserSolutions(payload);
      setSolutions(data.solutions || newSolutions);
      return data;
    } catch (error) {
      console.error('Failed to toggle solution:', error);
      setSolutions(solutions); // revert
      return null;
    }
  }, [isAuthenticated, solutions]);

  const reorderSolutions = useCallback(async (orderedSolutions) => {
    if (!isAuthenticated) return null;
    setSolutions(orderedSolutions);
    try {
      const payload = orderedSolutions.map(s => ({ nodeId: s.node_id, position: s.position }));
      const data = await apiSetUserSolutions(payload);
      setSolutions(data.solutions || orderedSolutions);
      return data;
    } catch (error) {
      console.error('Failed to reorder solutions:', error);
      return null;
    }
  }, [isAuthenticated]);

  // Fonctions pour les fonctionnalités utilisateur utilisant les vraies API
  const trackNodeVisit = async (nodeId, actionType = 'VIEW') => {
    if (!isAuthenticated) return null;
    
    try {
      console.log('Track visit:', nodeId, actionType);
      return await apiTrackNodeVisit(nodeId, actionType);
    } catch (error) {
      console.error('Failed to track node visit:', error);
      return null;
    }
  };

  const toggleBookmark = async (nodeId) => {
    if (!isAuthenticated) return null;
    
    try {
      console.log('Toggle bookmark:', nodeId);
      return await apiToggleBookmark(nodeId);
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      return null;
    }
  };

  const getUserBookmarks = async () => {
    if (!isAuthenticated) return [];
    
    try {
      console.log('Get user bookmarks');
      return await apiGetUserBookmarks();
    } catch (error) {
      console.error('Failed to get bookmarks:', error);
      return [];
    }
  };

  const rateNode = async (nodeId, rating, comment = null) => {
    if (!isAuthenticated) return null;
    
    try {
      console.log('Rate node:', nodeId, rating, comment);
      return await apiRateNode(nodeId, rating, comment);
    } catch (error) {
      console.error('Failed to rate node:', error);
      return null;
    }
  };

  const getUserRating = async (nodeId) => {
    if (!isAuthenticated) return { nodeId, rating: null };
    
    try {
      console.log('Get user rating:', nodeId);
      return await apiGetUserRating(nodeId);
    } catch (error) {
      console.error('Failed to get user rating:', error);
      return { nodeId, rating: null };
    }
  };

  const getNodeRatingStats = async (nodeId) => {
    if (!isAuthenticated) return { nodeId, hasRated: false };

    try {
      console.log('Get node rating stats:', nodeId);
      return await apiGetNodeRatingStats(nodeId);
    } catch (error) {
      console.error('Failed to get node rating stats:', error);
      return { nodeId, hasRated: false };
    }
  };

  // Reveal a card node (save to database when authenticated)
  const revealNode = async (nodeId) => {
    if (!isAuthenticated) return null;

    // Optimistically update local state
    setRevealedNodes(prev => new Set([...prev, nodeId]));

    try {
      return await apiRevealNode(nodeId);
    } catch (error) {
      console.error('Failed to reveal node:', error);
      // Revert on error
      setRevealedNodes(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      return null;
    }
  };

  // Check if a node is revealed
  const isNodeRevealed = (nodeId) => {
    return revealedNodes.has(nodeId);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    refreshUser,
    updateUserPreference,
    trackNodeVisit,
    toggleBookmark,
    getUserBookmarks,
    rateNode,
    getUserRating,
    getNodeRatingStats,
    revealedNodes,
    revealNode,
    isNodeRevealed,
    solutions,
    toggleSolution,
    reorderSolutions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};